import { useState, useEffect } from "react";

export interface VisualSettings {
  showAreaGradient: boolean;
  showGlyphs: boolean;
  showCrosshair: boolean;
  showGridRows: boolean;
  showGridColumns: boolean;
  showPatternBackground: boolean;
  smoothLines: boolean;
  showXAxis: boolean;
  showYAxis: boolean;
}

const defaultSettings: VisualSettings = {
  showAreaGradient: true,
  showGlyphs: false,
  showCrosshair: true,
  showGridRows: true,
  showGridColumns: false,
  showPatternBackground: false,
  smoothLines: false,
  showXAxis: true,
  showYAxis: true,
};

export const useVisualSettings = (storageKey = "chart_visual_config") => {
  const [settings, setSettings] = useState<VisualSettings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const storedConfig = localStorage.getItem(storageKey);
      if (storedConfig) {
        const parsedConfig = JSON.parse(storedConfig);
        // Apply stored settings, falling back to defaults for any missing properties
        setSettings({
          showAreaGradient:
            parsedConfig.showAreaGradient ?? defaultSettings.showAreaGradient,
          showGlyphs: parsedConfig.showGlyphs ?? defaultSettings.showGlyphs,
          showCrosshair:
            parsedConfig.showCrosshair ?? defaultSettings.showCrosshair,
          showGridRows:
            parsedConfig.showGridRows ?? defaultSettings.showGridRows,
          showGridColumns:
            parsedConfig.showGridColumns ?? defaultSettings.showGridColumns,
          showPatternBackground:
            parsedConfig.showPatternBackground ??
            defaultSettings.showPatternBackground,
          smoothLines: parsedConfig.smoothLines ?? defaultSettings.smoothLines,
          showXAxis: parsedConfig.showXAxis ?? defaultSettings.showXAxis,
          showYAxis: parsedConfig.showYAxis ?? defaultSettings.showYAxis,
        });
      }
    } catch (error) {
      console.error("Error loading visual settings:", error);
      // On error, use defaults
      setSettings(defaultSettings);
    } finally {
      setIsLoaded(true);
    }
  }, [storageKey]);

  // Save settings to localStorage when they change (but only after loaded)
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    }
  }, [isLoaded, settings, storageKey]);

  // Create an updater function for each setting
  const updateSetting = <K extends keyof VisualSettings>(
    key: K,
    value: VisualSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Reset all settings to defaults
  const resetToDefaults = () => {
    setSettings(defaultSettings);
  };

  return {
    settings,
    isLoaded,
    updateSetting,
    resetToDefaults,
  };
};
